/**
 * AuraCoPilot - 24/7 Global Co-Pilot Chatbot
 * Always-available floating chatbot to guide users and suggest next actions.
 * Uses a distinctive pill FAB with "Ask Aura" so it stands out from other nav icons.
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';
import ConversationFeed from './ConversationFeed';
import FreePlanChatBanner from '../upgrade/FreePlanChatBanner';

const PANEL_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const AuraCoPilot = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [cards, setCards] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  // Get current section from location
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/visions')) return 'visions';
    if (path.includes('/cashflow')) return 'cashflow';
    if (path.includes('/learning')) return 'learning';
    if (path.includes('/community')) return 'community';
    if (path.includes('/game')) return 'game';
    return 'general';
  };

  // Load initial suggestion based on current page
  useEffect(() => {
    if (user?.user_id) {
      loadContextualSuggestion();
    }
  }, [location.pathname, user?.user_id]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && cards.length === 0) {
      const welcomeCard = {
        id: 'welcome',
        type: 'aura_reflection',
        title: 'Aura Co-Pilot',
        content: `Hey! I'm Aura, your 24/7 co-pilot and app guide for Axonion Aura Finance. I'm here to help you understand the app, navigate features, and get the most out of Axonion Aura Finance.\n\nI can help you with:\n• Understanding how to use different sections (Dashboard, Cashflow, Visions, Learning, Game)\n• Navigating the app and finding features\n• Getting started and setting things up\n• Answering questions about how Axonion Aura Finance works\n\nWhat would you like help with today?`,
        timestamp: new Date().toISOString()
      };
      setCards([welcomeCard]);
    }
  }, [isOpen]);

  const loadContextualSuggestion = async () => {
    try {
      const currentSection = getCurrentSection();
      const response = await axios.post('/api/aura-chat/copilot-suggestion', {
        current_section: currentSection,
        user_id: user?.user_id
      });

      if (response.data.success && response.data.suggestion) {
        setSuggestion(response.data.suggestion);
      }
    } catch (error) {
      console.error('Error loading suggestion:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    const userCard = {
      id: `user_${Date.now()}`,
      type: 'user_intent',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setCards((prev) => [...prev, userCard]);

    try {
      const currentSection = getCurrentSection();
      const response = await axios.post('/api/aura-chat/chat', {
        message: userMessage,
        section: 'copilot',
        context: {
          current_section: currentSection,
          current_path: location.pathname
        },
        user_context: {
          user_id: user?.user_id,
          current_section: currentSection,
          current_path: location.pathname
        }
      }, { timeout: 90000 });

      if (response.data.success && response.data.cards) {
        const incoming = response.data.cards.filter(
          (c) => c.type !== 'user_intent' && c.type !== 'user_message'
        );
        setCards((prev) => [...prev, ...incoming]);
        // Refresh suggestion after interaction
        loadContextualSuggestion();
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorCard = {
        id: `error_${Date.now()}`,
        type: 'aura_reflection',
        title: 'Oops',
        content: "I'm having a bit of trouble right now. Could you try asking again?",
        timestamp: new Date().toISOString()
      };
      setCards(prev => [...prev, errorCard]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (suggestion.action_type === 'navigate') {
      navigate(suggestion.action_path);
      setIsOpen(true);
      setIsMinimized(false);
    } else if (suggestion.action_type === 'chat') {
      setIsOpen(true);
      setIsMinimized(false);
      setInputMessage(suggestion.action_message);
      // Auto-send the suggestion message
      setTimeout(() => {
        const event = new Event('submit', { bubbles: true, cancelable: true });
        const form = document.querySelector('#copilot-form');
        if (form) {
          form.dispatchEvent(event);
        }
      }, 100);
    }
  };

  return (
    <div data-tour="aura-copilot" className="fixed bottom-6 right-6 z-50 flex flex-col items-end justify-end pointer-events-none" style={{ minWidth: 0, minHeight: 0 }}>
      <div className="pointer-events-auto">
      {/* Floating Button - solid pine pill */}
      <AnimatePresence>
        {isMinimized && (
          <motion.button
            {...PANEL_MOTION}
            onClick={() => {
              setIsMinimized(false);
              setIsOpen(true);
            }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-pine surface-dark font-medium text-body min-w-[56px] h-14"
            title="Ask Aura – Your 24/7 app guide"
          >
            <LifebuoyIcon className="w-5 h-5 shrink-0" aria-hidden />
            <span className="hidden sm:inline whitespace-nowrap">Ask Aura</span>
            {suggestion && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-warning rounded-full border-2 border-pine" aria-hidden />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            {...PANEL_MOTION}
            className="fixed bottom-6 right-6 z-50 w-[420px] h-[600px] surface-emphasis surface-dark flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 shrink-0 border-b border-hairline">
              <div className="flex items-center gap-3">
                <LifebuoyIcon className="w-5 h-5 text-secondary shrink-0" />
                <div>
                  <h3 className="font-display text-h2 text-primary">Ask Aura</h3>
                  <p className="text-body text-secondary">24/7 app guide</p>
                </div>
              </div>
              <button
                onClick={() => setIsMinimized(true)}
                className="icon-btn"
                aria-label="Minimize Ask Aura"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Contextual Suggestion Banner */}
            {suggestion && (
              <div className="p-3 border-b border-hairline bg-pine-tint light-surface shrink-0">
                <p className="text-label text-secondary mb-1">Suggested Next Action:</p>
                <button
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-body text-pine hover:underline font-medium text-left w-full"
                >
                  {suggestion.message}
                </button>
              </div>
            )}

            {/* Conversation Feed — min-h-0 required for flex scroll */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden [&_.aura-card]:light-surface [&_.aura-card]:bg-bone-raised [&_.aura-card]:border-hairline">
              <ConversationFeed
                cards={cards}
                user={user}
                section="copilot"
                loading={loading}
                onSaveNote={() => {}}
              />
            </div>

            <FreePlanChatBanner />

            {/* Input */}
            <form id="copilot-form" onSubmit={handleSendMessage} className="p-4 border-t border-[color:var(--text-secondary)] border-opacity-30 shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask me anything about the app..."
                  className="flex-1 px-3 py-2 rounded-md border border-[color:var(--text-secondary)] border-opacity-40 bg-pine/30 text-primary placeholder:text-secondary focus:outline-none focus:ring-2 focus:ring-[color:var(--text-primary)] text-body"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || loading}
                  className="px-4 py-2 rounded-md bg-bone text-pine-deep font-medium text-body disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-300"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default AuraCoPilot;
